import dayjs from "dayjs";
import { getKeyResults } from "../../api/search-dashboard";

export const getProgressColor = (status: any) => {
  if (status === "Not Started")
    return { mainColor: "#999893", lightColor: "#E9E9E8" }; // Gray
  if (status === "In Progress - At Risk")
    return { mainColor: "#FFCA00", lightColor: "#FDF2D1" }; // Yellow
  if (status === "In Progress - Off Track")
    return { mainColor: "#F15B59", lightColor: "#FFF3F3" }; // Red
  if (status === "In Progress - On Track")
    return { mainColor: "#45B184", lightColor: "#E5F7EF" }; // Green
  if (status === "Completed")
    return { mainColor: "#0E9BF2", lightColor: "#CEECFF" }; // Blue
  if (status === "On Hold")
    return { mainColor: "#6B6862", lightColor: "#BAB9B6" }; // Gray
  if (status === "Cancelled")
    return { mainColor: "#8D5DF1", lightColor: "#F6F2FF" }; // Gray
  return { mainColor: "#efebecff", lightColor: "#efebecff" }; // Gray
};

export const formatObjectivePayload = (
  newObjectiveData: any,
  objectiveId?: number,
) => {
  const ownersKey = objectiveId ? "ownerIds" : "owners";

  // Calculate startDate and endDate based on selected year if year is provided
  let startDate, endDate;
  if (newObjectiveData.year) {
    startDate = dayjs()
      .year(newObjectiveData.year)
      .month(0)
      .date(1)
      .format("YYYY-MM-DD"); // January 1st
    endDate = dayjs()
      .year(newObjectiveData.year)
      .month(11)
      .date(31)
      .format("YYYY-MM-DD"); // December 31st
  } else {
    startDate = dayjs(newObjectiveData.startDate).format("YYYY-MM-DD");
    endDate = dayjs(newObjectiveData.endDate).format("YYYY-MM-DD");
  }

  return {
    objectiveName: newObjectiveData.objectiveName,
    year: newObjectiveData.year,
    createdAt: dayjs(newObjectiveData.createdAt || dayjs()).format(
      "YYYY-MM-DD",
    ),
    startDate: startDate,
    endDate: endDate,
    [ownersKey]:
      newObjectiveData.owners?.filter((ownerId: any) => ownerId?.ownerId) ??
      newObjectiveData?.ownerIds?.filter((ownerId: any) => ownerId?.ownerId) ??
      [],
  };
};

export const formatObjectiveResponse = (data: any[]) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item: any) => ({
    id: item.pk,
    objectiveName: item.objectiveName,
    objectiveDescription: item.objectiveDescription || "", // fallback if null
    startDate: item.startDate,
    endDate: item.endDate,
    year: item.year,
    createdAt: item.createdAt,
    status: item.status,
    currentOwner: item.currentOwner || { ownerId: null, name: "" }, // fallback if null
    owners: item.owners || [], // fallback to empty array
  }));
};
export const formatKeyResultPayloadData = (formValues: any) => {
  let owners = [];
  if (Array.isArray(formValues.ownerId)) {
    owners = formValues.ownerId.map((ownerId: any) => ({ ownerId }));
  } else if (formValues.ownerId) {
    owners = [{ ownerId: formValues.ownerId }];
  }

  return {
    title: formValues.title,
    description:
      typeof formValues.description === "string"
        ? formValues.description
        : formValues.description?.description || "",
    statusId: formValues.statusId,
    startDate: dayjs(formValues.startDate || dayjs()).format("YYYY-MM-DD"),
    endDate: dayjs(formValues.endDate || dayjs()).format("YYYY-MM-DD"),
    objectives: formValues?.selectedObjectiveData,

    owners,

    weightage: Number(formValues.weightage) || 0,
    comments: formValues.comments || "",
  };
};

export const formatKeyResultPayload = (
  formValues: any,
  objectiveResponse: any,
  keyResultId?: number,
  ownerIDList?: any,
) => {
  let ownerIdsData: any = {
    owners:
      formValues.ownerId?.map((ownerId: number) => ({
        ownerId: ownerId,
      })) || [],
  };

  if (keyResultId) {
    if (ownerIDList?.length > 0) {
      ownerIdsData = {
        ownerIds: [
          ...formValues.ownerId?.map((idCheck: any) => ({
            ownerId: idCheck,
            isRemove: false,
          })) || [],
          ...ownerIDList
            ?.map((idCheck: any) => {
              const checkID = formValues.ownerId?.find(
                (id: any) => id === idCheck,
              );
              return checkID ? null : { ownerId: idCheck, isRemove: true };
            })
            .filter((id: any) => id !== null) || [],
        ],
      };
    } else {
      ownerIdsData = {
        ownerIds: [
          ...formValues.ownerId?.map((idCheck: any) => ({
            ownerId: idCheck,
            isRemove: false,
          })) || [],
        ],
      };
    }
  }

  return {
    title: formValues.title,
    description:
      typeof formValues.description === "string"
        ? formValues.description
        : formValues.description?.description || "", // handle object/string
    startDate: dayjs(formValues.startDate || dayjs()).format("YYYY-MM-DD"),
    endDate: dayjs(formValues.endDate || dayjs()).format("YYYY-MM-DD"),
    weightage: Number(formValues.weightage) || 0,
    comments: formValues.comments,
    objectives: [...formValues?.selectedObjectiveData || [], ...(formValues?.deletedObjectiveData?.map((item: any) => ({ ...item, isRemove: true })) || [])],
    ...ownerIdsData,
  };
};

export const formatKeyResultResponse = (data: any) => {
  if (!data) return [];
  const dataArray = Array.isArray(data) ? data : [data];
  return dataArray.map((item: any) => ({
    id: item.pk || null,
    title: item.title || "",
    description: item.description || "",
    objectiveId: item.objectiveId || null,
    status: item.status,
    startDate: item.startDate || "",
    endDate: item.endDate || "",
    weightage: item.weightage || 0,
    owners: item.owners || [],
    comments: item.comments || "",
    initiatives: item.initiatives || [],
  }));
};
export const formatInitiativePayload = async (
  formValues: any,
  objectiveId: number,
  keyResultId: number,
) => {
  const existingKeyResultsData = await getKeyResults();
  const objectIds =
    (existingKeyResultsData?.data?.data || [])?.find(
      (key: any) => key?.pk === keyResultId,
    )?.objectiveId || [];
  return {
    //objectiveId,
    // keyResultId,
    title: formValues.title,
    departmentIds: formValues.participatingDepartments || [1],
    owners:
      formValues.owners?.filter((id: any) => id !== null) ||
      [formValues.owners]?.filter((id: any) => id !== null) ||
      [],
    startDate: dayjs(formValues.startDate || dayjs()).format("YYYY-MM-DD"),
    endDate: dayjs(formValues.endDate || dayjs()).format("YYYY-MM-DD"),
    threshold: Number(formValues.threshold) || 0,
    status: formValues.status.statusName,
    comments: formValues.comments,
    objectives: [
      {
        objectiveId: objectIds || [], // wrapped inside an array
        keyResultId: keyResultId,
        weightage: Number(formValues.weightage) || 0,
      },
    ],
  };
};

export const formatInitiativeResponse = (initiative: any) => {
  return {
    id: initiative.id,
    title: initiative.title,
    weightage: initiative.weightage,
    threshold: initiative.threshold,
    status: initiative.status,
    comments: initiative.comments,
    startDate: initiative.startDate,
    endDate: initiative.endDate,
    ownerIds: initiative.ownerIds,
    departmentIds: initiative.departmentIds,
  };
};
